import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { startLoadingProfesionales } from '../../../actions/profesionales';
import { startCancelarSuscripcion } from '../../../actions/suscripciones';
import { YlmModalTwoButtons } from '../../../components/main/YlmModalTwoButtons';
import EditarFechaFinal from './EditarFechaFinal';

const EstadoSuscripcion = ({ suscripcion, onUpdate }) => {
  const dispatch = useDispatch();
  const { colores } = useSelector(state => state.ui);
  const { profesionales } = useSelector(state => state.profesionales);
  const { role } = useSelector(state => state.auth);
  const isSuperAdmin = role === 'SUPERADMIN_ROLE' || role === 'ADMIN_ROLE';
  
  const [modalCancelarAbierto, setModalCancelarAbierto] = useState(false);
  const [modalResponse, setModalResponse] = useState(0);

  useEffect(() => {
    const empresaId = typeof suscripcion.empresa === 'object'
      ? suscripcion.empresa?._id
      : suscripcion.empresa;

    if (empresaId) {
      dispatch(startLoadingProfesionales(empresaId));
    }
  }, [dispatch, suscripcion.empresa]);

  const profesionalesActivos = profesionales?.filter(p => !p.deleted).length || 1;
  const profesionalesVisibles = Math.max(1, profesionalesActivos);
  const profesionalesExtra = Math.max(0, profesionalesActivos - 1);

  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'activa':
        return '#4CAF50';
      case 'trial':
        return '#FF9800';
      case 'cancelada':
        return '#F44336';
      default:
        return colores.colorPrincipal;
    }
  };

  const plan = 'Plan Pro';
  const estado = suscripcion.estado || 'Desconocido';
  const autoRenovar = suscripcion.autoRenovar !== undefined ? suscripcion.autoRenovar : true;
  const estaCancelada = autoRenovar === false;
  const fechaFacturacion = suscripcion.fechaRenovaciónPeriodo
    ? new Date(suscripcion.fechaRenovaciónPeriodo).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      })
    : 'N/D';

  const PRECIO_BASE_PLAN = 34.90;
  const PRECIO_POR_PROFESIONAL = 8.00;

  const precioBaseReal = suscripcion.precioBase ?? 0;
  const precioPorProfesional = suscripcion.precioPorProfesional || PRECIO_POR_PROFESIONAL;
  const subtotalCalculado = precioBaseReal + profesionalesExtra * precioPorProfesional;
  const ivaCalculado = +(subtotalCalculado * 0.21).toFixed(2);
  const totalConIva = +(subtotalCalculado + ivaCalculado).toFixed(2);
  const subtotal = precioBaseReal > 0 ? (suscripcion.subtotal || subtotalCalculado) : 0;

  const handleCancelarSuscripcion = () => {
    setModalCancelarAbierto(true);
  };

  useEffect(() => {
    if (modalResponse === 1) {
      if (suscripcion?._id) {
        const empresaId = typeof suscripcion.empresa === 'object'
          ? suscripcion.empresa?._id
          : suscripcion.empresa;
        dispatch(startCancelarSuscripcion(suscripcion._id, empresaId));
      }
      setModalResponse(0);
      setModalCancelarAbierto(false);
    }
  }, [modalResponse, suscripcion, dispatch]);

  const modalData = {
    title: 'Cancelar suscripción',
    text: [
      '¿Estás seguro de que quieres cancelar tu suscripción?',
      '',
      `Tu suscripción seguirá activa hasta el ${fechaFacturacion} y tendrás acceso completo a todas las funcionalidades hasta entonces.`,
      '',
      'Después de esa fecha, la suscripción no se renovará automáticamente y perderás el acceso al panel de administración.'
    ],
    buttonText: 'Sí, cancelar',
    button2Text: 'No, mantener',
    customStyles: true
  };

  return (
    <section className="section datos">
      <h3 style={{ color: colores.colorPrincipal }}>Estado de la suscripción</h3>

      <div className="datos-row row-1">
        <div className="datos-col col-1">
          <div className="data-item nombre">
            <label>Plan actual</label>
            <input type="text" value={plan} readOnly />
          </div>

          <div className="data-item estado">
            <label>Estado</label>
            <span
              className="estado-chip"
              style={{ backgroundColor: estaCancelada ? getEstadoColor('cancelada') : getEstadoColor(estado) }}
            >
              {estaCancelada ? 'cancelada' : estado.toLowerCase()}
            </span>
          </div>
        </div>

        <div className="datos-col col-2">
          <div className="data-item">
            <label>Próxima facturación</label>
            {isSuperAdmin ? (
              <EditarFechaFinal suscripcion={suscripcion} onUpdate={onUpdate} />
            ) : (
              <input type="text" value={fechaFacturacion} readOnly />
            )}
          </div>
        </div>
      </div>

      <div className="datos-row row-2">
        <div className="datos-col col-1">
          <div className="data-item">
            <label>Precio base del plan</label>
            <input type="text" value={`${PRECIO_BASE_PLAN.toFixed(2)}€/mes`} readOnly />
          </div>

          <div className="data-item">
            <label>Profesionales activos</label>
            <input type="text" value={profesionalesVisibles} readOnly />
          </div>

          <div className="data-item">
            <label>Coste por profesional</label>
            <input type="text" value={`${PRECIO_POR_PROFESIONAL.toFixed(2)}€/mes`} readOnly />
          </div>
        </div>

        <div className="datos-col col-2">
          <div className="data-item facturado">
            <label>Total facturado</label>
            <div
              style={{
                color: colores.colorPrincipal,
                fontFamily: 'var(--archivo_bold)',
                fontSize: '18px',
                fontWeight: 'bold',
                marginBottom: '7px',
              }}
            >
              {precioBaseReal > 0 ? `${totalConIva.toFixed(2)}€` : '0.00€'}
            </div>
            <small style={{ color: '#666', fontSize: '12px' }}>
              (Incluye 1 profesional. Subtotal: {subtotal.toFixed(2)}€, IVA incluido)
            </small>
          </div>
        </div>
      </div>

      <div className="suscripcion-cancel-section">
        {estaCancelada && estado === 'activa' ? (
          <div className="suscripcion-warning-banner">
            <div className="warning-content">
              <p>
                Tu suscripción está cancelada. Seguirás teniendo acceso hasta el <strong>{fechaFacturacion}</strong>.
              </p>
            </div>
          </div>
        ) : estado === 'activa' ? (
          <div className="suscripcion-cancel-actions">
            <p className="cancel-info-text">
              Si cancelas tu suscripción, seguirás teniendo acceso hasta el final del período actual.
            </p>
            <button
              onClick={handleCancelarSuscripcion}
              className="btn btn-cancel-suscripcion"
            >
              Cancelar suscripción
            </button>
          </div>
        ) : null}
      </div>

      <YlmModalTwoButtons
        data={modalData}
        setModalResponse={setModalResponse}
        modalTwoButtonsOpened={modalCancelarAbierto}
        setModalTwoButtonsOpened={setModalCancelarAbierto}
      />
    </section>
  );
};

export default EstadoSuscripcion;
